import {
  TextInput,
  Textarea,
  Button,
  Group,
  Stack,
  Select,
  Title,
  Grid,
  Paper,
  Text,
  Table,
  Radio,
  Tooltip,
  NumberInput,
  Checkbox,
  Flex,
} from "@mantine/core";
import { DatePickerInput, YearPickerInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";
import {
  IconLock,
  IconLockOpen,
  IconKey,
  IconCopy,
  IconAlertCircle,
  IconPrinter,
} from "@tabler/icons-react";
import { ObjectData } from "../types/ObjectFormTypes";
import FileUploadList from "../FileUploadList";
import ViewModeFileList from "../ViewModeFileList";
import DrawingControls from "../../drawing/DrawingControls";
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import { useState, useEffect } from "react";
import {
  getContractsByAddressSystemAndUser,
  getStatusByAddressSystem,
  getTypeByAddressSystem,
  getForemanByContract,
  getContractorForemanByContract,
  getOfficeUsersByContract,
  getPavementActionTypesByAddressSystem,
  getPavementGroundTypesByAddressSystem,
  getQualityControlByAddressSystem,
  getGuaranteeFollowByAddressSystem,
  getProjectManagersByContract,
  getSubscribersByAddressSystem,
  getRolesByAddressSystem,
  copyObjectToContract,
  updatePolyline,
  updatePolypoint,
  updatePolygon,
} from "../../../services/MapComponentsService";
import { searchGeometries } from "../../../services/GeometrySearchService";
import { notifications } from "@mantine/notifications";
import useTransientStore from "../../../data/transcientStore";
import usePermissions from "../../../hooks/usePermissions";
import { useAddressSystemFeatures } from "../../../hooks/useAddressSystemFeatures";
import { convertObjectDataToUpdateRequest } from "../../../utilities/GeometryConverters";
import { useTranslation } from "../../../hooks/useTranslation";
import { pdf } from "@react-pdf/renderer";
import { PlannedWorkStepsPDF } from "../../pdf/PlannedWorkStepsPDF";
import { saveAs } from "file-saver";
import {
  sanitizeForFilename,
  generateTimestamp,
} from "../../../utilities/PdfUtils";
import WKT from "ol/format/WKT";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import { Style, Stroke, Fill } from "ol/style";
import { Map as OLMap, View } from "ol";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import { getContractById } from "../../../services/ContractService";
import {
  getWorkPhaseNames,
  getAllWorkPhaseItems,
  getAllOtherWorkPhaseItems,
} from "../../../services/objectmodelservice";
import { WorkPhaseItemDto } from "../../../types/WorkPhaseItem";
import { WorkStep } from "../types/ObjectFormTypes";

interface InformationTabProps {
  form: UseFormReturnType<ObjectData>;
  showSuccessAlert: boolean;
  handleSubmit: (values: ObjectData) => void;
  handleClose: () => void;
  mode?: "view" | "create";
  addressSystemId?: number;
  userId?: number;
  onGeometryUpdate?: (wkt: string, drawingType: string) => void;
  onDrawingStart?: () => void;
  onDrawingStop?: () => void;
  onGeometrySaved?: () => void;
  initialWkt?: string;
  canModify?: boolean;
  isUpdating?: boolean;
  setIsUpdating?: (value: boolean) => void;
  stickyButtons?: boolean;
}

export default function InformationTab({
  form,
  showSuccessAlert,
  handleSubmit,
  handleClose,
  mode = "view",
  addressSystemId,
  userId,
  onGeometryUpdate,
  onDrawingStart,
  onDrawingStop,
  onGeometrySaved,
  initialWkt,
  isUpdating = false,
  setIsUpdating,
  canModify = true,
  stickyButtons = false,
}: InformationTabProps) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 334,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const [jobOptions, setJobOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [statusOptions, setStatusOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [typeOptions, setTypeOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingType, setIsLoadingType] = useState(false);
  const [foremanOptions, setForemanOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingForeman, setIsLoadingForeman] = useState(false);
  const [contractorForemanOptions, setContractorForemanOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingContractorForeman, setIsLoadingContractorForeman] =
    useState(false);
  const [officeUserOptions, setOfficeUserOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingOfficeUsers, setIsLoadingOfficeUsers] = useState(false);
  const [pavementActionOptions, setPavementActionOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingPavementAction, setIsLoadingPavementAction] = useState(false);
  const [pavementGroundOptions, setPavementGroundOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingPavementGround, setIsLoadingPavementGround] = useState(false);
  const [tilelayer, setTilelayer] = useState<"osm" | "satellite">("osm");
  const triggerClearDrawing = useTransientStore(
    (state) => state.triggerClearDrawing,
  );

  const handleGeometryCaptured = (wkt: string, drawingType: string) => {
    if (mode === "create") {
      // Crate mode - update form directly
      form.setFieldValue("wkt", wkt);
      form.setFieldValue("drawingType", drawingType);
    } else {
      // View mode - use callback to inform parent
      if (onGeometryUpdate) {
        onGeometryUpdate(wkt, drawingType);
      }
    }
  };
  const [qualityControlOptions, setQualityControlOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingQualityControl, setIsLoadingQualityControl] = useState(false);
  const [warrantyFollowUpOptions, setWarrantyFollowUpOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingWarrantyFollowUp, setIsLoadingWarrantyFollowUp] =
    useState(false);
  const [projectManagerOptions, setProjectManagerOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingProjectManager, setIsLoadingProjectManager] = useState(false);
  const [subscriberOptions, setSubscriberOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const [isLoadingSubscriber, setIsLoadingSubscriber] = useState(false);
  const [availableRoles, setAvailableRoles] = useState<
    { id: number; description: string }[]
  >([]);
  const [isLoadingRoles, setIsLoadingRoles] = useState(false);
  const [isInitialMount, setIsInitialMount] = useState(true);
  const [isCopying, setIsCopying] = useState(false);

  // Get current user from store at component level
  const currentUser = useTransientStore((state) => state.currentUser);
  const currentUserId = useTransientStore((state) => state.currentUserId);
  const { checkAccess } = usePermissions();
  const { hasStaraObjectFields } = useAddressSystemFeatures();
  const canEditDrawings = checkAccess("CITY_OBJECT_DRAWINGS_MODIFY");
  const canShowQualityControlFiles = checkAccess("CITY_OBJECT_QC_FILE_SHOW");
  const canModifyQualityControlFiles = checkAccess(
    "CITY_OBJECT_QC_FILE_MODIFY",
  );
  const canModifyQualityControlDropdown = checkAccess(
    "CITY_OBJECT_QC_STATUS_MODIFY",
  );
  const canShowOtherFiles = checkAccess("CITY_OBJECT_OTHER_FILE_SHOW");
  const canModifyOtherFiles = checkAccess("CITY_OBJECT_OTHER_FILE_MODIFY");

  const fieldLabel = (
    defaultKey: string,
    defaultFallback: string,
    staraKey?: string,
    staraFallback?: string,
  ) =>
    hasStaraObjectFields && staraKey
      ? getTranslation(staraKey) || staraFallback || defaultFallback
      : getTranslation(defaultKey) || defaultFallback;

  const mapLookupOptions = (
    data: Array<{ id: number; description?: string; name?: string }>,
    fallbackPrefix: string,
  ) =>
    data
      .map((item) => ({
        value: item.id?.toString() || "",
        label:
          item.description ||
          item.name ||
          `${fallbackPrefix} ${item.id}`,
      }))
      .filter((opt) => opt.value !== "");

  // Mark initial mount as complete after first render
  useEffect(() => {
    setIsInitialMount(false);
  }, []);

  // Fetch contracts when component mounts or when addressSystemId/userId changes
  useEffect(() => {
    const fetchContracts = async () => {
      if (!addressSystemId || !userId) {
        setJobOptions([]);
        return;
      }

      setIsLoadingJobs(true);
      try {
        const contracts = await getContractsByAddressSystemAndUser(
          addressSystemId,
          userId,
        );
        const options = (
          contracts as Array<{ id: number; name?: string; shortName?: string }>
        )
          .map((contract) => ({
            value: contract.id?.toString() || "",
            label:
              contract.name ||
              contract.shortName ||
              `${getTranslation("LABEL_CONTRACT") || "Contract"} ${contract.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setJobOptions(options);
        // Initialize jobName/contractName if a job is already selected (string or number)
        try {
          const currentJobIdRaw: unknown = form?.values?.job;
          const currentJobId =
            typeof currentJobIdRaw === "number"
              ? String(currentJobIdRaw)
              : typeof currentJobIdRaw === "string" &&
                  currentJobIdRaw.length > 0
                ? currentJobIdRaw
                : null;
          if (currentJobId) {
            const match = options.find((opt) => opt.value === currentJobId);
            if (match && typeof match.label === "string") {
              form.setFieldValue("jobName" as any, match.label);
              form.setFieldValue("contractName" as any, match.label);
            }
          }
        } catch {}
      } catch (error) {
        console.error("Failed to fetch contracts:", error);
        setJobOptions([]);
      } finally {
        setIsLoadingJobs(false);
      }
    };

    fetchContracts();
  }, [addressSystemId, userId]);

  // Ensure jobName/contractName is kept in sync whenever options or selected job change
  useEffect(() => {
    try {
      const currentJobIdRaw: unknown = form?.values?.job;
      const currentJobId =
        typeof currentJobIdRaw === "number"
          ? String(currentJobIdRaw)
          : typeof currentJobIdRaw === "string" && currentJobIdRaw.length > 0
            ? currentJobIdRaw
            : null;
      if (!currentJobId) return;
      const match = (jobOptions || []).find(
        (opt) => opt.value === currentJobId,
      );
      if (match && typeof match.label === "string") {
        // Only update if missing or different to avoid churn
        const currentJobName = (form.values as any).jobName as
          | string
          | undefined;
        const currentContractName = (form.values as any).contractName as
          | string
          | undefined;
        if (currentJobName !== match.label) {
          form.setFieldValue("jobName" as any, match.label);
        }
        if (currentContractName !== match.label) {
          form.setFieldValue("contractName" as any, match.label);
        }
      }
    } catch {}
  }, [jobOptions, form.values.job]);

  // Fetch status options when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchStatus = async () => {
      if (!addressSystemId) {
        setStatusOptions([]);
        return;
      }

      setIsLoadingStatus(true);
      try {
        const statusData = await getStatusByAddressSystem(addressSystemId);
        const options = (
          statusData as Array<{
            id: number;
            name?: string;
            defaultValue?: number;
            description?: string;
          }>
        )
          .map((status) => ({
            value: status.id?.toString() || "",
            label:
              status.name ||
              status.description ||
              `${getTranslation("LABEL_STATUS") || "Status"} ${status.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setStatusOptions(options);
      } catch (error) {
        console.error("Failed to fetch status data:", error);
        setStatusOptions([]);
      } finally {
        setIsLoadingStatus(false);
      }
    };

    fetchStatus();
  }, [addressSystemId]);

  // Fetch type options when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchType = async () => {
      if (!addressSystemId) {
        setTypeOptions([]);
        return;
      }

      setIsLoadingType(true);
      try {
        const typeData = await getTypeByAddressSystem(addressSystemId);
        const options = (typeData as Array<{ id: number; name?: string }>)
          .map((type) => ({
            value: type.id?.toString() || "",
            label:
              type.name ||
              `${getTranslation("LABEL_TYPE") || "Type"} ${type.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setTypeOptions(options);
      } catch (error) {
        console.error("Failed to fetch type data:", error);
        setTypeOptions([]);
      } finally {
        setIsLoadingType(false);
      }
    };

    fetchType();
  }, [addressSystemId]);

  // Fetch foreman options when component mounts or when selected job/contract changes
  useEffect(() => {
    const fetchForeman = async () => {
      const selectedJobId = form.values.job;
      if (!selectedJobId) {
        setForemanOptions([]);
        if (!isInitialMount) {
          form.setFieldValue("foreman", null);
        }
        return;
      }

      setIsLoadingForeman(true);
      try {
        const foremanData = await getForemanByContract(parseInt(selectedJobId));
        const options = (
          foremanData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
          }>
        )
          .map((foreman) => ({
            value: foreman.id?.toString() || "",
            label:
              foreman.name ||
              `${foreman.firstName || ""} ${foreman.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_FOREMAN") || "Foreman"} ${foreman.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setForemanOptions(options);
      } catch (error) {
        console.error("Failed to fetch foreman data:", error);
        setForemanOptions([]);
      } finally {
        setIsLoadingForeman(false);
      }
    };

    fetchForeman();
  }, [form.values.job]);

  // Fetch contractor foreman options when component mounts or when selected job/contract changes
  useEffect(() => {
    const fetchContractorForeman = async () => {
      const selectedJobId = form.values.job;
      if (!selectedJobId) {
        setContractorForemanOptions([]);
        if (!isInitialMount) {
          form.setFieldValue("contractorsForeman", "");
        }
        return;
      }

      setIsLoadingContractorForeman(true);
      try {
        const contractorForemanData = await getContractorForemanByContract(
          parseInt(selectedJobId),
        );
        const options = (
          contractorForemanData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
          }>
        )
          .map((foreman) => ({
            value: foreman.id?.toString() || "",
            label:
              foreman.name ||
              `${foreman.firstName || ""} ${foreman.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_CONTRACTORS_FOREMAN") || "Contractor Foreman"} ${foreman.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setContractorForemanOptions(options);
      } catch (error) {
        console.error("Failed to fetch contractor foreman data:", error);
        setContractorForemanOptions([]);
      } finally {
        setIsLoadingContractorForeman(false);
      }
    };

    fetchContractorForeman();
  }, [form.values.job]);

  useEffect(() => {
    const fetchOfficeUsers = async () => {
      if (!hasStaraObjectFields) {
        setOfficeUserOptions([]);
        return;
      }

      const selectedJobId = form.values.job;
      if (!selectedJobId) {
        setOfficeUserOptions([]);
        if (!isInitialMount) {
          form.setFieldValue("officeUser", "");
        }
        return;
      }

      setIsLoadingOfficeUsers(true);
      try {
        const officeUserData = await getOfficeUsersByContract(
          parseInt(selectedJobId),
        );
        const options = (
          officeUserData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
          }>
        )
          .map((user) => ({
            value: user.id?.toString() || "",
            label:
              user.name ||
              `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_BILLING_USER") || "Billing user"} ${user.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setOfficeUserOptions(options);
      } catch (error) {
        console.error("Failed to fetch office users:", error);
        setOfficeUserOptions([]);
      } finally {
        setIsLoadingOfficeUsers(false);
      }
    };

    fetchOfficeUsers();
  }, [form.values.job, hasStaraObjectFields]);

  useEffect(() => {
    const fetchPavementLookups = async () => {
      if (!hasStaraObjectFields || !addressSystemId) {
        setPavementActionOptions([]);
        setPavementGroundOptions([]);
        return;
      }

      setIsLoadingPavementAction(true);
      setIsLoadingPavementGround(true);
      try {
        const [actionData, groundData] = await Promise.all([
          getPavementActionTypesByAddressSystem(addressSystemId),
          getPavementGroundTypesByAddressSystem(addressSystemId),
        ]);
        setPavementActionOptions(
          mapLookupOptions(
            actionData as Array<{
              id: number;
              description?: string;
              name?: string;
            }>,
            getTranslation("LABEL_PAVING_PROCEDURE") || "Paving procedure",
          ),
        );
        setPavementGroundOptions(
          mapLookupOptions(
            groundData as Array<{
              id: number;
              description?: string;
              name?: string;
            }>,
            getTranslation("LABEL_PLATFORM") || "Platform",
          ),
        );
      } catch (error) {
        console.error("Failed to fetch pavement lookups:", error);
        setPavementActionOptions([]);
        setPavementGroundOptions([]);
      } finally {
        setIsLoadingPavementAction(false);
        setIsLoadingPavementGround(false);
      }
    };

    fetchPavementLookups();
  }, [addressSystemId, hasStaraObjectFields]);

  // Fetch quality control options when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchQualityControl = async () => {
      if (!addressSystemId) {
        setQualityControlOptions([]);
        return;
      }

      setIsLoadingQualityControl(true);
      try {
        const qualityControlData =
          await getQualityControlByAddressSystem(addressSystemId);
        const options = (
          qualityControlData as Array<{
            id: number;
            description?: string;
            name?: string;
            firstName?: string;
            lastName?: string;
          }>
        )
          .map((qc) => ({
            value: qc.id?.toString() || "",
            label:
              qc.description ||
              qc.name ||
              `${qc.firstName || ""} ${qc.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_QUALITY_CONTROL") || "Quality Control"} ${qc.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setQualityControlOptions(options);
      } catch (error) {
        console.error("Failed to fetch quality control data:", error);
        setQualityControlOptions([]);
      } finally {
        setIsLoadingQualityControl(false);
      }
    };

    fetchQualityControl();
  }, [addressSystemId]);

  // Fetch warranty follow up options when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchWarrantyFollowUp = async () => {
      if (!addressSystemId) {
        setWarrantyFollowUpOptions([]);
        return;
      }

      setIsLoadingWarrantyFollowUp(true);
      try {
        const warrantyFollowUpData =
          await getGuaranteeFollowByAddressSystem(addressSystemId);
        const options = (
          warrantyFollowUpData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
          }>
        )
          .map((wfu) => ({
            value: wfu.id?.toString() || "",
            label:
              wfu.name ||
              `${wfu.firstName || ""} ${wfu.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_WARRANTY_FOLLOW_UP") || "Warranty Follow Up"} ${wfu.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setWarrantyFollowUpOptions(options);
      } catch (error) {
        console.error("Failed to fetch warranty follow up data:", error);
        setWarrantyFollowUpOptions([]);
      } finally {
        setIsLoadingWarrantyFollowUp(false);
      }
    };

    fetchWarrantyFollowUp();
  }, [addressSystemId]);

  // Fetch project manager options when component mounts or when selected job/contract changes
  useEffect(() => {
    const fetchProjectManager = async () => {
      const selectedJobId = form.values.job;
      if (!selectedJobId) {
        setProjectManagerOptions([]);
        if (!isInitialMount) {
          form.setFieldValue("projectManager", null);
        }
        return;
      }

      setIsLoadingProjectManager(true);
      try {
        const projectManagerData = await getProjectManagersByContract(
          parseInt(selectedJobId),
        );
        const options = (
          projectManagerData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
            email?: string;
          }>
        )
          .map((pm) => ({
            value: pm.id?.toString() || "",
            label:
              pm.name ||
              `${pm.firstName || ""} ${pm.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_PROJECT_MANAGER") || "Project Manager"} ${pm.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setProjectManagerOptions(options);
      } catch (error) {
        console.error("Failed to fetch project manager data:", error);
        setProjectManagerOptions([]);
      } finally {
        setIsLoadingProjectManager(false);
      }
    };

    fetchProjectManager();
  }, [form.values.job]);

  // Fetch subscriber options when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchSubscriber = async () => {
      if (!addressSystemId) {
        setSubscriberOptions([]);
        return;
      }

      setIsLoadingSubscriber(true);
      try {
        const subscriberData =
          await getSubscribersByAddressSystem(addressSystemId);
        const options = (
          subscriberData as Array<{
            id: number;
            name?: string;
            firstName?: string;
            lastName?: string;
            email?: string;
          }>
        )
          .map((sub) => ({
            value: sub.id?.toString() || "",
            label:
              sub.name ||
              `${sub.firstName || ""} ${sub.lastName || ""}`.trim() ||
              `${getTranslation("LABEL_SUBSCRIBER") || "Subscriber"} ${sub.id}`,
          }))
          .filter((opt) => opt.value !== "");
        setSubscriberOptions(options);
      } catch (error) {
        console.error("Failed to fetch subscriber data:", error);
        setSubscriberOptions([]);
      } finally {
        setIsLoadingSubscriber(false);
      }
    };

    fetchSubscriber();
  }, [addressSystemId]);

  // Fetch roles when component mounts or when addressSystemId changes
  useEffect(() => {
    const fetchRoles = async () => {
      if (!addressSystemId) {
        setAvailableRoles([]);
        return;
      }

      setIsLoadingRoles(true);
      try {
        const rolesData = await getRolesByAddressSystem(addressSystemId);
        const roles = rolesData as Array<{ id: number; description: string }>;
        setAvailableRoles(roles);
      } catch (error) {
        console.error("Failed to fetch roles:", error);
        setAvailableRoles([]);
      } finally {
        setIsLoadingRoles(false);
      }
    };

    fetchRoles();
  }, [addressSystemId]);

  useEffect(() => {
    if (!availableRoles.length) {
      return;
    }

    const currentPermissions = form.values.rolePermissions;

    const needsMerge =
      currentPermissions.length !== availableRoles.length ||
      !availableRoles.every((role) =>
        currentPermissions.some((p) => p.roleId === role.id),
      );

    if (needsMerge) {
      const mergedPermissions = availableRoles.map((role) => {
        const saved = currentPermissions.find((p) => p.roleId === role.id);
        return {
          roleId: role.id,
          roleName: role.description,
          accessLevel: saved?.accessLevel ?? 0,
        };
      });
      form.setFieldValue("rolePermissions", mergedPermissions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRoles, form.values.id]);

  const handleCopyToContract = async () => {
    // Validation
    if (!form.values.targetContract || form.values.targetContract === "") {
      notifications.show({
        title: getTranslation("TITLE_VALIDATION_ERROR") || "Validation Error",
        message:
          getTranslation("MESSAGE_SELECT_TARGET_CONTRACT") ||
          "Please select a target contract",
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-error",
        },
      });
      return;
    }

    if (!form.values.newObjectName || form.values.newObjectName.trim() === "") {
      notifications.show({
        title: getTranslation("TITLE_VALIDATION_ERROR") || "Validation Error",
        message:
          getTranslation("MESSAGE_ENTER_NEW_OBJECT_NAME") ||
          "Please enter a name for the new object",
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-error",
        },
      });
      return;
    }

    setIsCopying(true);
    try {
      await copyObjectToContract({
        perceptionId: form.values.id,
        targetContractId: parseInt(form.values.targetContract),
        newName: form.values.newObjectName.trim(),
        userId: currentUserId ?? 0,
      });

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_OBJECT_COPIED_SUCCESS") ||
          "Object copied to contract successfully",
        color: "green",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-success",
        },
      });

      // Clear the form fields
      form.setFieldValue("targetContract", "");
      form.setFieldValue("newObjectName", "");

      // Refresh geometries after copy - preserve user's existing search filters
      try {
        const state = useTransientStore.getState();
        const { selectedAddressSystem, geometrySearchFilters } = state;

        if (selectedAddressSystem?.id && userId) {
          // Refetch search results with existing filters (user's current view)
          const searchResponse = await searchGeometries(
            selectedAddressSystem.id,
            geometrySearchFilters,
          );

          state.updateSearchResults(
            searchResponse.results,
            searchResponse.polylines,
            searchResponse.polygons,
            searchResponse.points,
          );
        }
      } catch (error) {
        console.error("Failed to refresh geometries after copy:", error);
      }
    } catch (error) {
      console.error("Failed to copy object to contract:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_COPY_OBJECT_FAILED") ||
          `Failed to copy object to contract: ${errorMessage}`,
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-error",
        },
      });
    } finally {
      setIsCopying(false);
    }
  };

  // Helper function to capture map image focused on the object
  const captureMapImage = async (): Promise<string | undefined> => {
    try {
      // Get the main map from Zustand store (for reference only)
      const mainMap = useTransientStore.getState().map;

      if (!mainMap) {
        console.warn("Map not found in store");
        return undefined;
      }

      if (!form.values.wkt) {
        console.warn("No WKT geometry to capture");
        return undefined;
      }

      //console.log('[captureMapImage] Creating off-screen map for capture...');

      // Parse the geometry from WKT
      const format = new WKT();
      let geometry;
      try {
        geometry = format.readGeometry(form.values.wkt, {
          dataProjection: "EPSG:3067",
          featureProjection: "EPSG:3857",
        });
      } catch (error) {
        console.error("Error parsing WKT geometry:", error);
        return undefined;
      }

      // Create an off-screen container for the temporary map
      const offScreenContainer = document.createElement("div");
      offScreenContainer.style.position = "absolute";
      offScreenContainer.style.left = "-9999px";
      offScreenContainer.style.top = "-9999px";
      offScreenContainer.style.width = "800px";
      offScreenContainer.style.height = "600px";
      document.body.appendChild(offScreenContainer);

      // Create the feature with the object's geometry
      const tempSource = new VectorSource();
      const feature = new Feature({ geometry });

      // Style the feature
      feature.setStyle(
        new Style({
          stroke: new Stroke({
            color: "#3388ff",
            width: 3,
          }),
          fill: new Fill({
            color: "rgba(51, 136, 255, 0.2)",
          }),
        }),
      );

      tempSource.addFeature(feature);

      const vectorLayer = new VectorLayer({
        source: tempSource,
      });

      // Create a fresh tile layer (OSM)
      const tileLayer = new TileLayer({
        source: new OSM(),
      });

      // Create a new map instance just for capturing
      const captureMap = new OLMap({
        target: offScreenContainer,
        layers: [tileLayer, vectorLayer],
        view: new View({
          projection: "EPSG:3857",
        }),
      });

      // Fit the view to the geometry extent
      const extent = geometry.getExtent();
      captureMap.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: 18,
      });

      //console.log('[captureMapImage] Off-screen map created, waiting for tiles to load...');

      // Wait for the map to fully render (tiles to load)
      await new Promise<void>((resolve) => {
        let tilesLoading = true;
        let checkCount = 0;
        const maxChecks = 50; // 5 seconds max

        const checkTilesLoaded = () => {
          checkCount++;
          const tileSource = tileLayer.getSource();

          // Check if tiles are still loading
          if (tileSource) {
            const loading = (tileSource as any).tileCache?.getCount?.() === 0;
            if (!loading || checkCount >= maxChecks) {
              tilesLoading = false;
            }
          }

          if (!tilesLoading || checkCount >= maxChecks) {
            resolve();
          } else {
            setTimeout(checkTilesLoaded, 100);
          }
        };

        // Initial render
        captureMap.renderSync();

        // Start checking after a short delay
        setTimeout(checkTilesLoaded, 500);
      });

      // Force a final render
      captureMap.renderSync();

      // Additional wait to ensure everything is painted
      await new Promise((resolve) => setTimeout(resolve, 500));

      captureMap.renderSync();

      //console.log('[captureMapImage] Capturing canvas...');

      // Get the canvas from the off-screen map
      const mapViewport = offScreenContainer.querySelector(".ol-viewport");
      const canvas = mapViewport?.querySelector(
        "canvas",
      ) as HTMLCanvasElement | null;

      let imageUrl: string | undefined;

      if (canvas && canvas.width > 0 && canvas.height > 0) {
        //console.log(`[captureMapImage] Canvas size: ${canvas.width}x${canvas.height}`);
        imageUrl = canvas.toDataURL("image/png", 0.8);
        //console.log('[captureMapImage] Image captured successfully');
      } else {
        console.warn("[captureMapImage] Canvas not found or has zero size");
      }

      // Clean up: dispose the map and remove the container
      captureMap.setTarget(undefined);
      document.body.removeChild(offScreenContainer);

      return imageUrl;
    } catch (error) {
      console.warn("Failed to capture map image:", error);
      return undefined;
    }
  };

  const printPlannedWorkSteps = async () => {
    try {
      // Show loading notification
      const loadingNotification = notifications.show({
        loading: true,
        title: "Generating PDF",
        message: "Capturing images and preparing document...",
        autoClose: false,
        withCloseButton: false,
      });

      // Fetch contract data if available
      let contractData = null;
      const contractId = form.values.job;
      const objectId = form.values.id;

      if (!contractId || !objectId) {
        notifications.hide(loadingNotification);
        notifications.show({
          title: "Error",
          message:
            "Contract or Object is not selected. Please select them before printing.",
          color: "red",
          autoClose: 5000,
          position: "top-center",
        });
        return;
      }

      if (contractId) {
        try {
          const selectedAddressSystem =
            useTransientStore.getState().selectedAddressSystem;
          const addressSystemIdForContract =
            addressSystemId || selectedAddressSystem?.id || 1;
          contractData = await getContractById(
            Number(contractId),
            addressSystemIdForContract,
          );
        } catch (error) {
          console.error("Failed to fetch contract data:", error);
        }
      }

      // Fetch planned work steps from backend (same logic as PlannedWorkStepsTab)
      const groups = await getWorkPhaseNames(Number(contractId), objectId, 0);

      let plannedWorkSteps: WorkStep[] = [];

      if (!groups?.length) {
        // Load "Others" items if no groups
        const othersRows =
          (
            await getAllOtherWorkPhaseItems(Number(contractId), objectId, 0)
          )?.filter(
            (r) => typeof r.workItemId === "number" && r.workItemId > 0,
          ) || [];

        // Add group header for Others
        if (othersRows.length > 0) {
          plannedWorkSteps.push({
            id: null as any,
            typeOfCoating: "Others",
            unit: "",
            amount: 0,
            surfaceArea: 0,
            tons: 0,
            unitPrice: 0,
            priceC: 0,
            isGroupHeader: true,
          });
        }

        plannedWorkSteps.push(
          ...othersRows.map((item: WorkPhaseItemDto) => ({
            id: item.workItemId,
            typeOfCoating: item.name,
            unit: item.unitName,
            amount: item.itemCount ?? 0,
            surfaceArea: item.itemArea ?? 0,
            tons: item.itemWeight ?? 0,
            unitPrice: Number(item.unitPrice ?? 0),
            priceC: Number(item.totalPrice ?? 0),
            isGroupHeader: false,
          })),
        );
      } else {
        // Fetch all groups
        const targetGroupIds: number[] = groups.map((g) => g.id);
        const allRowsArrays = await Promise.all([
          ...targetGroupIds.map((gid) =>
            getAllWorkPhaseItems(gid, Number(contractId), objectId, 0),
          ),
          getAllOtherWorkPhaseItems(Number(contractId), objectId, 0),
        ]);

        // Build items with group headers
        targetGroupIds.forEach((gid, idx) => {
          const gname = groups.find((g) => g.id === gid)?.name ?? "";
          const rows = (allRowsArrays[idx] ?? []).filter(
            (r) => typeof r.workItemId === "number" && r.workItemId > 0,
          );

          if (rows.length > 0) {
            // Add group header
            plannedWorkSteps.push({
              id: null as any,
              typeOfCoating: gname,
              unit: "",
              amount: 0,
              surfaceArea: 0,
              tons: 0,
              unitPrice: 0,
              priceC: 0,
              isGroupHeader: true,
            });

            // Add group items
            plannedWorkSteps.push(
              ...rows.map((item: WorkPhaseItemDto) => ({
                id: item.workItemId,
                typeOfCoating: item.name,
                unit: item.unitName,
                amount: item.itemCount ?? 0,
                surfaceArea: item.itemArea ?? 0,
                tons: item.itemWeight ?? 0,
                unitPrice: Number(item.unitPrice ?? 0),
                priceC: Number(item.totalPrice ?? 0),
                isGroupHeader: false,
              })),
            );
          }
        });

        // Add Others group if there are items
        const othersRows = (
          allRowsArrays[allRowsArrays.length - 1] ?? []
        ).filter((r) => typeof r.workItemId === "number" && r.workItemId > 0);
        if (othersRows.length > 0) {
          plannedWorkSteps.push({
            id: null as any,
            typeOfCoating: "Others",
            unit: "",
            amount: 0,
            surfaceArea: 0,
            tons: 0,
            unitPrice: 0,
            priceC: 0,
            isGroupHeader: true,
          });

          plannedWorkSteps.push(
            ...othersRows.map((item: WorkPhaseItemDto) => ({
              id: item.workItemId,
              typeOfCoating: item.name,
              unit: item.unitName,
              amount: item.itemCount ?? 0,
              surfaceArea: item.itemArea ?? 0,
              tons: item.itemWeight ?? 0,
              unitPrice: Number(item.unitPrice ?? 0),
              priceC: Number(item.totalPrice ?? 0),
              isGroupHeader: false,
            })),
          );
        }
      }

      if (plannedWorkSteps.length === 0) {
        notifications.hide(loadingNotification);
        notifications.show({
          title: "No Data",
          message: "No planned work steps found to print.",
          color: "orange",
          autoClose: 5000,
          position: "top-center",
        });
        return;
      }

      // Capture images dynamically
      const mapImageUrl = await captureMapImage();

      // Generate PDF
      const blob = await pdf(
        <PlannedWorkStepsPDF
          plannedWorkSteps={plannedWorkSteps}
          form={form}
          objectId={form.values.id}
          contractId={contractId ? Number(contractId) : undefined}
          contract={contractData}
          mapImageUrl={mapImageUrl}
          objectImageUrl={undefined} // Only using map image of the object
        />,
      ).toBlob();

      // Cleanup blob URLs if they were created
      if (mapImageUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(mapImageUrl);
      }

      // Trigger download
      const location =
        form?.values?.streetName || form?.values?.name || "unknown";
      const sanitizedLocation = sanitizeForFilename(location);
      const timestamp = generateTimestamp();
      const fileName = `object_report_${sanitizedLocation}_${timestamp}.pdf`;
      saveAs(blob, fileName);

      notifications.hide(loadingNotification);
      notifications.show({
        title: "Success",
        message: "Object report generated successfully!",
        color: "green",
        autoClose: 5000,
        position: "top-center",
      });
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      notifications.show({
        title: "Error",
        message: "Failed to generate object report. Please try again.",
        color: "red",
        autoClose: 5000,
        position: "top-center",
      });
    }
  };

  const handleSaveAndRefresh = async () => {
    form.validate();
    if (!form.isValid()) return;

    setIsUpdating?.(true);
    try {
      const values = form.values;
      const geometryType = values.drawingType.toLowerCase();

      // Convert FileUploadItem to FileUploadRequest
      const convertFilesToUploadRequests = async (
        files: any[],
        fileType?: number,
      ) => {
        if (!files || files.length === 0) return null;

        const fileRequests = await Promise.all(
          files.map(async (fileItem) => {
            // Convert File to base64
            const base64Data = await new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onloadend = () => {
                const base64 = (reader.result as string).split(",")[1];
                resolve(base64);
              };
              reader.onerror = reject;
              reader.readAsDataURL(fileItem.file);
            });

            return {
              fileName: fileItem.file.name,
              fileHeader: fileItem.fileHeader || "",
              fileData: base64Data,
              contentType: fileItem.file.type,
              fileType: fileType || 0,
            };
          }),
        );

        return fileRequests;
      };

      // Process regular files
      const regularFiles = await convertFilesToUploadRequests(
        values.attachedFiles,
        0,
      );

      // Process qc files
      const qcFiles = await convertFilesToUploadRequests(
        values.coatingResearchReport,
        99,
      );

      const updatePayload = convertObjectDataToUpdateRequest(
        values,
        currentUserId,
        currentUser,
        regularFiles,
        qcFiles,
      );

      let result;
      if (geometryType.includes("line") || geometryType.includes("polyline")) {
        result = await updatePolyline(updatePayload);
      } else if (geometryType.includes("point")) {
        result = await updatePolypoint(updatePayload);
      } else if (geometryType.includes("polygon")) {
        result = await updatePolygon(updatePayload);
      } else {
        throw new Error(`Unknown geometry type: ${geometryType}`);
      }

      // Clear the drawing layer immediately after successful update
      triggerClearDrawing();

      // Notify parent that geometry was saved
      onGeometrySaved?.();

      notifications.show({
        title: getTranslation("TITLE_SUCCESS") || "Success",
        message:
          getTranslation("MESSAGE_OBJECT_UPDATED") ||
          "Object updated successfully!",
        color: "green",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-success",
        },
      });

      // Refresh geometries after update using search filters to prevent flash
      try {
        const state = useTransientStore.getState();
        const { selectedAddressSystem, geometrySearchFilters } = state;

        if (selectedAddressSystem?.id && addressSystemId && userId) {
          // Preserve user's existing search filters (both create and update modes)
          const searchResponse = await searchGeometries(
            selectedAddressSystem.id,
            geometrySearchFilters,
          );

          state.updateSearchResults(
            searchResponse.results,
            searchResponse.polylines,
            searchResponse.polygons,
            searchResponse.points,
          );
        }
      } catch (error) {
        console.error("Failed to refresh geometries after update:", error);
      }

      const store = useTransientStore.getState();
      const allGeometries = [
        ...store.currentPolylines,
        ...store.currentPolygons,
        ...store.currentPolypoints,
      ];

      const updatedGeometry = allGeometries.find((geo) => geo.id === values.id);

      if (updatedGeometry) {
        const updatedObjectData = {
          ...values,
          name: updatedGeometry.name || values.name,
          foreman:
            updatedGeometry.foremanId && updatedGeometry.foremanName
              ? {
                  id: updatedGeometry.foremanId,
                  name: updatedGeometry.foremanName,
                  email: updatedGeometry.foremanEmail,
                }
              : values.foreman,
          contractorForeman:
            updatedGeometry.contractorForemanId &&
            updatedGeometry.contractorForemanName
              ? {
                  id: updatedGeometry.contractorForemanId,
                  name: updatedGeometry.contractorForemanName,
                  email: updatedGeometry.contractorForemanEmail,
                }
              : values.contractorForeman,
          projectManager:
            updatedGeometry.projectManagerId &&
            updatedGeometry.projectManagerName
              ? {
                  id: updatedGeometry.projectManagerId,
                  name: updatedGeometry.projectManagerName,
                  email: updatedGeometry.projectManagerEmail,
                }
              : values.projectManager,
          subscriber:
            updatedGeometry.subscriberId && updatedGeometry.subscriberName
              ? {
                  id: updatedGeometry.subscriberId,
                  name: updatedGeometry.subscriberName,
                  email: updatedGeometry.subscriberEmail,
                }
              : values.subscriber,
          existingFiles: updatedGeometry.files || [],
          existingQcFiles: updatedGeometry.qcFiles || [],
          fileIdsToKeep: updatedGeometry.files?.map((f) => f.id) || [],
          qcFileIdsToKeep: updatedGeometry.qcFiles?.map((f) => f.id) || [],
          attachedFiles: [],
          coatingResearchReport: [],
          modifier: updatedGeometry.modifier || currentUser,
          modified: updatedGeometry.modified
            ? new Date(updatedGeometry.modified)
            : new Date(),
          checkedToInvoiceDate: updatedGeometry.checkedToInvoiceDate
            ? new Date(updatedGeometry.checkedToInvoiceDate)
            : values.checkedToInvoice
              ? new Date()
              : null,
          checkedToInvoiceUserName:
            updatedGeometry.checkedToInvoiceUserName ||
            (values.checkedToInvoice ? currentUser : null),
        };

        form.setValues(updatedObjectData);
      } else {
        const updatedValues = {
          ...values,
          modifier: currentUser,
          modified: new Date(),
          attachedFiles: [],
          coatingResearchReport: [],
          checkedToInvoiceDate:
            values.checkedToInvoice && !values.checkedToInvoiceDate
              ? new Date()
              : values.checkedToInvoiceDate,
          checkedToInvoiceUserName:
            values.checkedToInvoice && !values.checkedToInvoiceUserName
              ? currentUser
              : values.checkedToInvoiceUserName,
        };

        form.setValues(updatedValues);
      }

      // Fetch updated search results
      const state = useTransientStore.getState();
      const { selectedAddressSystem, geometrySearchFilters } = state;

      if (selectedAddressSystem?.id && geometrySearchFilters) {
        const searchResponse = await searchGeometries(
          selectedAddressSystem.id,
          geometrySearchFilters,
        );

        state.updateSearchResults(
          searchResponse.results,
          searchResponse.polylines,
          searchResponse.polygons,
          searchResponse.points,
        );
      }

      //console.log("Object updated successfully:", result);
    } catch (error) {
      console.error("Error updating object:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      notifications.show({
        title: getTranslation("TITLE_ERROR") || "Error",
        message:
          getTranslation("MESSAGE_UPDATE_OBJECT_FAILED") ||
          `Failed to update object: ${errorMessage}`,
        color: "red",
        autoClose: 5000,
        position: "top-center",
        classNames: {
          root: "notification-error",
        },
      });
    } finally {
      setIsUpdating?.(false);
    }
  };

  // Helper function to convert empty string to null for Select components (for string fields)
  const getSelectProps = (fieldName: keyof ObjectData) => {
    const props = form.getInputProps(fieldName);
    return {
      ...props,
      value: props.value === "" ? null : props.value,
      onChange: (value: string | null) => {
        form.setFieldValue(fieldName as any, value || "");
        // When job (contract) changes, also store its display label for use in other tabs
        if (fieldName === "job") {
          try {
            const selected = (typeof value === "string" ? value : null) ?? null;
            if (selected) {
              const match = (jobOptions || []).find(
                (opt) => opt.value === selected,
              );
              if (match && typeof match.label === "string") {
                form.setFieldValue("jobName" as any, match.label);
                form.setFieldValue("contractName" as any, match.label);
              } else {
                // Clear name if not found to avoid showing raw IDs
                form.setFieldValue("jobName" as any, "");
              }
            } else {
              form.setFieldValue("jobName" as any, "");
            }
          } catch {}
        }
      },
    };
  };

  return (
    <Stack gap="lg">
      {/* {showSuccessAlert && (
                    <Alert
                        icon={<IconCheck size={16} />}
                        title={getTranslation("TITLE_SUCCESS") || "Success!"}
                        color="green"
                        variant="light"
                    >
                        Object information has been updated successfully.
                    </Alert>
                )} */}

      <DrawingControls
        onGeometryCaptured={handleGeometryCaptured}
        mode={mode}
        initialWkt={initialWkt}
        onDrawingStart={onDrawingStart}
        onDrawingStop={onDrawingStop}
        showTileLayerSwitch={mode === "create"}
        tilelayer={tilelayer}
        onTileLayerChange={setTilelayer}
        instanceId={
          mode === "create" ? "addaction-drawing" : "objectform-drawing"
        }
        disabled={
          mode === "create" ? !canModify : !canModify || !canEditDrawings
        }
      />

      {/* Drawing Type Section */}
      {/* {mode === 'view' && (
                    <Paper p="md" withBorder>
                        <Title order={4} mb="md" c="blue">Drawing Type</Title>
                        <TextInput
                            label={getTranslation("LABEL_GEOMETRY_TYPE") || "Geometry Type"}
                            value={form.values.drawingType}
                            disabled
                            description="Inferred from WKT geometry string"
                        />
                    </Paper>
                )} */}

      {/* Procedure Details Section */}
      <Paper p="md" withBorder>
        <Title order={6} mb="md" c="blue">
          {getTranslation("TITLE_PROCEDURE_DETAILS") || "Procedure Details"}
        </Title>
        <Stack gap="md">
          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <Select
                label={getTranslation("LABEL_JOB") || "Job"} //done [HttpGet("contracts/{addressSystemId}/user/{userId}")]
                placeholder={
                  isLoadingJobs
                    ? getTranslation("LABEL_LOADING_CONTRACTS") ||
                      "Loading contracts..."
                    : jobOptions.length === 0
                      ? getTranslation("LABEL_NO_JOBS_AVAILABLE") ||
                        "No jobs available"
                      : getTranslation("PLACEHOLDER_SELECT_JOB") || "Select job"
                }
                data={jobOptions}
                disabled={
                  !canModify || isLoadingJobs || jobOptions.length === 0
                }
                required
                clearable
                {...getSelectProps("job")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <TextInput
                label={fieldLabel(
                  "LABEL_NAME",
                  "Name",
                  "LABEL_PROPERTY_NAME",
                  "Property name",
                )}
                placeholder={
                  fieldLabel(
                    "PLACEHOLDER_ENTER_NAME",
                    "Enter name",
                    "PLACEHOLDER_ENTER_PROPERTY_NAME",
                    "Enter property name",
                  )
                }
                // required
                withAsterisk
                disabled={!canModify}
                {...form.getInputProps("name")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <TextInput
                label={getTranslation("LABEL_STREET_NAME") || "Street Name"}
                placeholder={
                  getTranslation("PLACEHOLDER_ENTER_STREET_NAME") ||
                  "Enter street name"
                }
                disabled={!canModify}
                {...form.getInputProps("streetName")}
              />
            </Grid.Col>
          </Grid>

          {hasStaraObjectFields && (
            <Grid>
              <Grid.Col span={mode === "create" ? 12 : 4}>
                <TextInput
                  label={getTranslation("LABEL_KP_NUMBER") || "KP-number"}
                  placeholder={
                    getTranslation("PLACEHOLDER_ENTER_KP_NUMBER") ||
                    "Enter KP-number"
                  }
                  disabled={!canModify}
                  {...form.getInputProps("ordererReferenceNumber")}
                />
              </Grid.Col>
              <Grid.Col span={mode === "create" ? 12 : 4}>
                <NumberInput
                  label={
                    getTranslation("LABEL_STREET_DISTANCE_START") ||
                    "Street distance start"
                  }
                  placeholder={
                    getTranslation("PLACEHOLDER_ENTER_START_DISTANCE") ||
                    "Start"
                  }
                  min={0}
                  decimalScale={0}
                  disabled={!canModify}
                  {...form.getInputProps("startDistance")}
                />
              </Grid.Col>
              <Grid.Col span={mode === "create" ? 12 : 4}>
                <NumberInput
                  label={
                    getTranslation("LABEL_STREET_DISTANCE_END") ||
                    "Street distance end"
                  }
                  placeholder={
                    getTranslation("PLACEHOLDER_ENTER_END_DISTANCE") || "End"
                  }
                  min={0}
                  decimalScale={0}
                  disabled={!canModify}
                  {...form.getInputProps("endDistance")}
                />
              </Grid.Col>
            </Grid>
          )}

          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <TextInput
                label={fieldLabel(
                  "LABEL_ORDER_NAME",
                  "Order Name",
                  "LABEL_ORDER_NUMBER",
                  "Order number",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_ENTER_ORDER_NAME") ||
                  "Enter order name"
                }
                disabled={!canModify}
                {...form.getInputProps("orderName")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <TextInput
                label={fieldLabel(
                  "LABEL_NETWORK_NAME",
                  "Network Name",
                  "LABEL_NETWORK_NUMBER_STEP",
                  "Network number/step",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_ENTER_NETWORK_NAME") ||
                  "Enter network name"
                }
                disabled={!canModify}
                {...form.getInputProps("networkName")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <Select
                label={getTranslation("LABEL_STATUS") || "Status"} //done [HttpGet("states/{addressSystemId}")]
                placeholder={
                  isLoadingStatus
                    ? getTranslation("LABEL_LOADING_STATUS") ||
                      "Loading status..."
                    : statusOptions.length === 0
                      ? getTranslation("LABEL_NO_STATUS_OPTIONS") ||
                        "No status options available"
                      : getTranslation("PLACEHOLDER_SELECT_STATUS") ||
                        "Select status"
                }
                data={statusOptions}
                disabled={
                  !canModify || isLoadingStatus || statusOptions.length === 0
                }
                required
                clearable
                {...getSelectProps("status")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <Select
                label={getTranslation("LABEL_TYPE") || "Type"} //done [HttpGet("strength/{addressSystemId}")]
                placeholder={
                  isLoadingType
                    ? getTranslation("LABEL_LOADING_TYPES") ||
                      "Loading types..."
                    : typeOptions.length === 0
                      ? getTranslation("LABEL_NO_TYPES_AVAILABLE") ||
                        "No types available"
                      : getTranslation("PLACEHOLDER_SELECT_TYPE") ||
                        "Select type"
                }
                data={typeOptions}
                disabled={
                  !canModify || isLoadingType || typeOptions.length === 0
                }
                required
                clearable
                {...getSelectProps("type")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <YearPickerInput
                label={getTranslation("LABEL_PLANNED_YEAR") || "Planned Year"}
                placeholder={
                  getTranslation("PLACEHOLDER_SELECT_YEAR") || "Select year"
                }
                size="sm"
                disabled={!canModify}
                styles={{
                  calendarHeader: {
                    fontSize: "14px",
                  },
                  calendarHeaderControl: {
                    fontSize: "14px",
                    width: "24px",
                    height: "24px",
                    minWidth: "24px",
                    minHeight: "28px",
                  },
                  calendarHeaderLevel: {
                    fontSize: "14px",
                    fontWeight: 500,
                  },
                  yearsList: {
                    fontSize: "13px",
                  },
                  yearsListCell: {
                    fontSize: "13px",
                  },
                }}
                {...form.getInputProps("plannedYear")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 4}>
              <TextInput
                label={fieldLabel(
                  "LABEL_MEASURES",
                  "Measures",
                  "LABEL_PROCEDURE",
                  "Procedure",
                )}
                placeholder={
                  fieldLabel(
                    "PLACEHOLDER_ENTER_MEASURES",
                    "Enter measures",
                    "PLACEHOLDER_ENTER_PROCEDURE",
                    "Enter procedure",
                  )
                }
                disabled={!canModify}
                {...form.getInputProps("measures")}
              />
            </Grid.Col>
          </Grid>

          {hasStaraObjectFields && (
            <Grid>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <Select
                  label={
                    getTranslation("LABEL_PAVING_PROCEDURE") ||
                    "Paving procedure"
                  }
                  placeholder={
                    isLoadingPavementAction
                      ? getTranslation("LABEL_LOADING_PAVING_PROCEDURE") ||
                        "Loading paving procedures..."
                      : pavementActionOptions.length === 0
                        ? getTranslation("LABEL_NO_PAVING_PROCEDURES") ||
                          "No paving procedures available"
                        : getTranslation("PLACEHOLDER_SELECT_PAVING_PROCEDURE") ||
                          "Select paving procedure"
                  }
                  data={pavementActionOptions}
                  disabled={
                    !canModify ||
                    isLoadingPavementAction ||
                    pavementActionOptions.length === 0
                  }
                  clearable
                  {...getSelectProps("pavementActionType")}
                />
              </Grid.Col>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <Select
                  label={getTranslation("LABEL_PLATFORM") || "Platform"}
                  placeholder={
                    isLoadingPavementGround
                      ? getTranslation("LABEL_LOADING_PLATFORM") ||
                        "Loading platforms..."
                      : pavementGroundOptions.length === 0
                        ? getTranslation("LABEL_NO_PLATFORMS") ||
                          "No platforms available"
                        : getTranslation("PLACEHOLDER_SELECT_PLATFORM") ||
                          "Select platform"
                  }
                  data={pavementGroundOptions}
                  disabled={
                    !canModify ||
                    isLoadingPavementGround ||
                    pavementGroundOptions.length === 0
                  }
                  clearable
                  {...getSelectProps("pavementGroundType")}
                />
              </Grid.Col>
            </Grid>
          )}

          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <TextInput
                label={getTranslation("LABEL_LANES") || "Lanes"}
                placeholder={
                  getTranslation("PLACEHOLDER_ENTER_LANES") || "Enter lanes"
                }
                disabled={!canModify}
                {...form.getInputProps("lanes")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <TextInput
                label={fieldLabel(
                  "LABEL_LEVEL_OF_READINESS",
                  "Level of Readiness",
                  "LABEL_DEGREE_OF_READINESS",
                  "Degree of readiness",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_ENTER_LEVEL_OF_READINESS") ||
                  "Enter level of readiness"
                }
                disabled={!canModify}
                {...form.getInputProps("levelOfReadiness")}
              />
            </Grid.Col>
          </Grid>

          {/* <Title order={5} mt="md">Personnel</Title> */}
          {(!hasStaraObjectFields ||
            subscriberOptions.length > 0 ||
            form.values.subscriber) && (
          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <Select
                label={getTranslation("LABEL_SUBSCRIBER") || "Subscriber"} //done [HttpGet("subscribers/{addressSystemId}")]
                placeholder={
                  isLoadingSubscriber
                    ? getTranslation("LABEL_LOADING_SUBSCRIBERS") ||
                      "Loading subscribers..."
                    : subscriberOptions.length === 0
                      ? getTranslation("LABEL_NO_SUBSCRIBERS_AVAILABLE") ||
                        "No subscribers available"
                      : getTranslation("PLACEHOLDER_SELECT_SUBSCRIBER") ||
                        "Select subscriber"
                }
                data={subscriberOptions}
                disabled={
                  !canModify ||
                  isLoadingSubscriber ||
                  subscriberOptions.length === 0
                }
                clearable
                value={form.values.subscriber?.id.toString() || null}
                onChange={(value) => {
                  if (!value) {
                    form.setFieldValue("subscriber", null);
                  } else {
                    const selected = subscriberOptions.find(
                      (opt) => opt.value === value,
                    );
                    if (selected) {
                      form.setFieldValue("subscriber", {
                        id: parseInt(value),
                        name: selected.label,
                        email: undefined, // Email will be populated from backend response if available
                      });
                    }
                  }
                }}
                error={form.errors.subscriber}
              />
            </Grid.Col>
            {mode === "view" && !hasStaraObjectFields && (
              <Grid.Col span={6}>
                <Flex direction="column" justify="center" h="100%">
                  <Checkbox
                    label={
                      getTranslation("LABEL_APPROVED_FOR_INVOICING") ||
                      "Approved for Invoicing"
                    }
                    checked={form.values.checkedToInvoice}
                    onChange={(event) =>
                      form.setFieldValue(
                        "checkedToInvoice",
                        event.currentTarget.checked,
                      )
                    }
                  />
                  {form.values.checkedToInvoice &&
                    form.values.checkedToInvoiceDate && (
                      <Text size="xs" c="dimmed" mt={4}>
                        {getTranslation("LABEL_APPROVED_ON") || "Approved on"}{" "}
                        {new Date(
                          form.values.checkedToInvoiceDate,
                        ).toLocaleDateString()}
                        {form.values.checkedToInvoiceUserName &&
                          ` ${getTranslation("LABEL_BY") || "by"} ${form.values.checkedToInvoiceUserName}`}
                      </Text>
                    )}
                </Flex>
              </Grid.Col>
            )}
          </Grid>
          )}

          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <Select
                label={getTranslation("LABEL_FOREMAN") || "Foreman"} //done [HttpGet("foreman/{contractId}")]
                placeholder={
                  isLoadingForeman
                    ? getTranslation("LABEL_LOADING_FOREMEN") ||
                      "Loading foremen..."
                    : !form.values.job
                      ? getTranslation("LABEL_SELECT_JOB_FIRST") ||
                        "Select a job first"
                      : foremanOptions.length === 0
                        ? getTranslation("LABEL_NO_FOREMEN_AVAILABLE") ||
                          "No foremen available"
                        : getTranslation("PLACEHOLDER_SELECT_FOREMAN") ||
                          "Select foreman"
                }
                data={foremanOptions}
                disabled={
                  !canModify ||
                  isLoadingForeman ||
                  !form.values.job ||
                  foremanOptions.length === 0
                }
                clearable
                value={form.values.foreman?.id.toString() || null}
                onChange={(value) => {
                  if (!value) {
                    form.setFieldValue("foreman", null);
                  } else {
                    const selected = foremanOptions.find(
                      (opt) => opt.value === value,
                    );
                    if (selected) {
                      form.setFieldValue("foreman", {
                        id: parseInt(value),
                        name: selected.label,
                      });
                    }
                  }
                }}
                error={form.errors.foreman}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <Select
                label={
                  getTranslation("LABEL_CONTRACTORS_FOREMAN") ||
                  "Contractors Foreman"
                } //done [HttpGet("contractorForeman/{contractId}")]
                placeholder={
                  isLoadingContractorForeman
                    ? getTranslation("LABEL_LOADING_CONTRACTOR_FOREMEN") ||
                      "Loading contractor foremen..."
                    : !form.values.job
                      ? getTranslation("LABEL_SELECT_JOB_FIRST") ||
                        "Select a job first"
                      : contractorForemanOptions.length === 0
                        ? getTranslation(
                            "LABEL_NO_CONTRACTOR_FOREMEN_AVAILABLE",
                          ) || "No contractor foremen available"
                        : getTranslation(
                            "PLACEHOLDER_SELECT_CONTRACTOR_FOREMAN",
                          ) || "Select contractors foreman"
                }
                data={contractorForemanOptions}
                disabled={
                  !canModify ||
                  isLoadingContractorForeman ||
                  !form.values.job ||
                  contractorForemanOptions.length === 0
                }
                clearable
                value={form.values.contractorForeman?.id.toString() || null}
                onChange={(value) => {
                  if (!value) {
                    form.setFieldValue("contractorForeman", null);
                  } else {
                    const selected = contractorForemanOptions.find(
                      (opt) => opt.value === value,
                    );
                    if (selected) {
                      form.setFieldValue("contractorForeman", {
                        id: parseInt(value),
                        name: selected.label,
                      });
                    }
                  }
                }}
                error={form.errors.contractorForeman}
              />
            </Grid.Col>
          </Grid>

          {hasStaraObjectFields && (
            <Grid>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <Select
                  label={getTranslation("LABEL_BILLING_USER") || "Billing user"}
                  placeholder={
                    isLoadingOfficeUsers
                      ? getTranslation("LABEL_LOADING_BILLING_USERS") ||
                        "Loading billing users..."
                      : !form.values.job
                        ? getTranslation("LABEL_SELECT_JOB_FIRST") ||
                          "Select a job first"
                        : officeUserOptions.length === 0
                          ? getTranslation("LABEL_NO_BILLING_USERS") ||
                            "No billing users available"
                          : getTranslation("PLACEHOLDER_SELECT_BILLING_USER") ||
                            "Select billing user"
                  }
                  data={officeUserOptions}
                  disabled={
                    !canModify ||
                    isLoadingOfficeUsers ||
                    !form.values.job ||
                    officeUserOptions.length === 0
                  }
                  clearable
                  {...getSelectProps("officeUser")}
                />
              </Grid.Col>
            </Grid>
          )}

          <Grid>
            {!hasStaraObjectFields && (
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <Select
                label={
                  getTranslation("LABEL_PROJECT_MANAGER") || "Project Manager"
                } //done [HttpGet("projectManagers/{contractId}")]
                placeholder={
                  isLoadingProjectManager
                    ? getTranslation("LABEL_LOADING_PROJECT_MANAGERS") ||
                      "Loading project managers..."
                    : !form.values.job
                      ? getTranslation("LABEL_SELECT_JOB_FIRST") ||
                        "Select a job first"
                      : projectManagerOptions.length === 0
                        ? getTranslation(
                            "LABEL_NO_PROJECT_MANAGERS_AVAILABLE",
                          ) || "No project managers available"
                        : getTranslation(
                            "PLACEHOLDER_SELECT_PROJECT_MANAGER",
                          ) || "Select project manager"
                }
                data={projectManagerOptions}
                disabled={
                  !canModify ||
                  isLoadingProjectManager ||
                  !form.values.job ||
                  projectManagerOptions.length === 0
                }
                clearable
                value={form.values.projectManager?.id.toString() || null}
                onChange={(value: string | null) => {
                  if (!value) {
                    form.setFieldValue("projectManager", null);
                    return;
                  }
                  const selected = projectManagerOptions.find(
                    (opt) => opt.value === value,
                  );
                  if (selected) {
                    form.setFieldValue("projectManager", {
                      id: parseInt(value),
                      name: selected.label,
                      email: undefined,
                    });
                  }
                }}
              />
            </Grid.Col>
            )}
            <Grid.Col span={mode === "create" || hasStaraObjectFields ? 12 : 6}>
              <Select
                label={fieldLabel(
                  "LABEL_QUALITY_CONTROL",
                  "Quality Control",
                  "LABEL_MASS_SAMPLES",
                  "Mass samples",
                )}
                placeholder={
                  isLoadingQualityControl
                    ? getTranslation("LABEL_LOADING_QUALITY_CONTROL") ||
                      "Loading quality control..."
                    : qualityControlOptions.length === 0
                      ? getTranslation("LABEL_NO_QUALITY_CONTROL_OPTIONS") ||
                        "No quality control options available"
                      : getTranslation("PLACEHOLDER_SELECT_QUALITY_CONTROL") ||
                        "Select quality control"
                }
                data={qualityControlOptions}
                disabled={
                  !canModify ||
                  isLoadingQualityControl ||
                  qualityControlOptions.length === 0 ||
                  !canModifyQualityControlDropdown
                }
                clearable
                {...getSelectProps("qualityControl")}
              />
            </Grid.Col>
          </Grid>

          {hasStaraObjectFields && (
            <Grid>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <Select
                  label={getTranslation("LABEL_DRILL_SAMPLES") || "Drill samples"}
                  placeholder={
                    isLoadingQualityControl
                      ? getTranslation("LABEL_LOADING_QUALITY_CONTROL") ||
                        "Loading quality control..."
                      : qualityControlOptions.length === 0
                        ? getTranslation("LABEL_NO_QUALITY_CONTROL_OPTIONS") ||
                          "No quality control options available"
                        : getTranslation("PLACEHOLDER_SELECT_DRILL_SAMPLES") ||
                          "Select drill samples"
                  }
                  data={qualityControlOptions}
                  disabled={
                    !canModify ||
                    isLoadingQualityControl ||
                    qualityControlOptions.length === 0 ||
                    !canModifyQualityControlDropdown
                  }
                  clearable
                  {...getSelectProps("qualityControl2")}
                />
              </Grid.Col>
            </Grid>
          )}

          <Textarea
            label={
              getTranslation("LABEL_QUALITY_CONTROL_COMMENTS") ||
              "Quality Control Comments"
            }
            placeholder={
              getTranslation("PLACEHOLDER_ENTER_QUALITY_CONTROL_COMMENTS") ||
              "Enter quality control comments"
            }
            minRows={3}
            maxRows={6}
            autosize
            disabled={!canModify}
            {...form.getInputProps("qualityControlComments")}
          />

          {hasStaraObjectFields && (
            <Grid>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <Checkbox
                  label={
                    getTranslation("LABEL_NEXT_CONTRACT_INFO") ||
                    "Next-contract info"
                  }
                  disabled={!canModify}
                  {...form.getInputProps("roadMarkings", { type: "checkbox" })}
                />
              </Grid.Col>
              <Grid.Col span={mode === "create" ? 12 : 6}>
                <TextInput
                  label={
                    getTranslation("LABEL_DRAWN_LENGTH_AREA") ||
                    "Drawn length/area"
                  }
                  readOnly
                  disabled
                  {...form.getInputProps("objectLength")}
                />
              </Grid.Col>
            </Grid>
          )}

          {/* <Title order={5} mt="md">Dates (PVM)</Title> */}
          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <DatePickerInput
                label={fieldLabel(
                  "LABEL_PVM",
                  "PVM",
                  "LABEL_GIVEN_TO_CONTRACTOR",
                  "Given to contractor",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                }
                valueFormat="DD.MM.YYYY"
                size="sm"
                disabled={!canModify}
                {...form.getInputProps("pvm")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <DatePickerInput
                label={fieldLabel(
                  "LABEL_AGREED_PVM",
                  "Agreed PVM",
                  "LABEL_AGREED",
                  "Agreed",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                }
                valueFormat="DD.MM.YYYY"
                size="sm"
                disabled={!canModify}
                {...form.getInputProps("agreedPvm")}
              />
            </Grid.Col>
          </Grid>

          <Grid>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <DatePickerInput
                label={fieldLabel(
                  "LABEL_COMPLETED_PVM",
                  "Completed PVM",
                  "LABEL_COMPLETED",
                  "Completed",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                }
                valueFormat="DD.MM.YYYY"
                size="sm"
                disabled={!canModify}
                {...form.getInputProps("completedPvm")}
              />
            </Grid.Col>
            <Grid.Col span={mode === "create" ? 12 : 6}>
              <DatePickerInput
                label={fieldLabel(
                  "LABEL_APPROVED_PVM",
                  "Approved PVM",
                  "LABEL_APPROVED",
                  "Approved",
                )}
                placeholder={
                  getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                }
                valueFormat="DD.MM.YYYY"
                size="sm"
                disabled={!canModify}
                {...form.getInputProps("approvedPvm")}
              />
            </Grid.Col>
          </Grid>

          {hasStaraObjectFields && (
            <>
              <Grid>
                <Grid.Col span={mode === "create" ? 12 : 4}>
                  <DatePickerInput
                    label={
                      getTranslation("LABEL_TRANSFER_REQUEST_EXPORTED") ||
                      "Transfer request exported"
                    }
                    placeholder={
                      getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                    }
                    valueFormat="DD.MM.YYYY"
                    size="sm"
                    disabled={!canModify}
                    {...form.getInputProps("vehicleRelocationNoticeAdded")}
                  />
                </Grid.Col>
                <Grid.Col span={mode === "create" ? 12 : 4}>
                  <DatePickerInput
                    label={
                      getTranslation("LABEL_TRANSFER_REQUEST_VALIDITY_START") ||
                      "Transfer request validity start"
                    }
                    placeholder={
                      getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                    }
                    valueFormat="DD.MM.YYYY"
                    size="sm"
                    disabled={!canModify}
                    {...form.getInputProps("vehicleRelocationNoticeStart")}
                  />
                </Grid.Col>
                <Grid.Col span={mode === "create" ? 12 : 4}>
                  <DatePickerInput
                    label={
                      getTranslation("LABEL_TRANSFER_REQUEST_VALIDITY_END") ||
                      "Transfer request validity end"
                    }
                    placeholder={
                      getTranslation("PLACEHOLDER_DDMMYYYY") || "DD.MM.YYYY"
                    }
                    valueFormat="DD.MM.YYYY"
                    size="sm"
                    disabled={!canModify}
                    {...form.getInputProps("vehicleRelocationNoticeEnd")}
                  />
                </Grid.Col>
              </Grid>

              {mode !== "create" && (
                <Grid>
                  <Grid.Col span={6}>
                    <TextInput
                      label={
                        getTranslation("LABEL_INVOICE_NUMBER") ||
                        "Invoice number"
                      }
                      placeholder={
                        getTranslation("PLACEHOLDER_ENTER_INVOICE_NUMBER") ||
                        "Enter invoice number"
                      }
                      disabled={!canModify}
                      {...form.getInputProps("invoiceNumber")}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <TextInput
                      label={
                        getTranslation("LABEL_INVOICING_PERMISSION_DATE") ||
                        "Invoicing permission date"
                      }
                      value={
                        form.values.allowedToInvoiceDate
                          ? new Date(
                              form.values.allowedToInvoiceDate,
                            ).toLocaleDateString()
                          : ""
                      }
                      disabled
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Checkbox
                      label={getTranslation("LABEL_INVOICED") || "Invoiced"}
                      disabled={!canModify}
                      {...form.getInputProps("invoiced", { type: "checkbox" })}
                    />
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Checkbox
                      label={
                        getTranslation("LABEL_BILLING_AUTHORIZATION") ||
                        "Billing authorization"
                      }
                      disabled={!canModify}
                      {...form.getInputProps("allowedToInvoice", {
                        type: "checkbox",
                      })}
                    />
                  </Grid.Col>
                </Grid>
              )}
            </>
          )}

          <Textarea
            label={getTranslation("LABEL_DESCRIPTION") || "Description"}
            placeholder={
              getTranslation("PLACEHOLDER_ENTER_DETAILED_DESCRIPTION") ||
              "Enter detailed description"
            }
            minRows={3}
            maxRows={6}
            autosize
            disabled={!canModify}
            {...form.getInputProps("description")}
          />

          <Select
            label={
              getTranslation("LABEL_WARRANTY_FOLLOW_UP") || "Warranty Follow Up"
            } // done [HttpGet("guaranteeFollow/{addressSystemId}")]
            placeholder={
              isLoadingWarrantyFollowUp
                ? getTranslation("LABEL_LOADING_WARRANTY_FOLLOW_UP") ||
                  "Loading warranty follow up..."
                : warrantyFollowUpOptions.length === 0
                  ? getTranslation("LABEL_NO_WARRANTY_FOLLOW_UP_OPTIONS") ||
                    "No warranty follow up options available"
                  : getTranslation("PLACEHOLDER_SELECT_WARRANTY_FOLLOW_UP") ||
                    "Select warranty follow up"
            }
            data={warrantyFollowUpOptions}
            disabled={
              !canModify ||
              isLoadingWarrantyFollowUp ||
              warrantyFollowUpOptions.length === 0
            }
            clearable
            {...getSelectProps("warrantyFollowUp")}
          />

          <Textarea
            label={
              getTranslation("LABEL_WARRANTY_FOLLOW_UP_COMMENTS") ||
              "Warranty Follow Up Comments"
            }
            placeholder={
              getTranslation("PLACEHOLDER_ENTER_WARRANTY_FOLLOW_UP_COMMENTS") ||
              "Enter warranty follow up comments"
            }
            minRows={3}
            maxRows={6}
            autosize
            disabled={!canModify}
            {...form.getInputProps("warrantyFollowUpComments")}
          />

          {hasStaraObjectFields && (
            <Checkbox
              label={getTranslation("LABEL_SHOW_IN_LOUHI") || "Show in Louhi"}
              disabled={!canModify}
              {...form.getInputProps("publishToApi", { type: "checkbox" })}
            />
          )}
        </Stack>
      </Paper>

      {/* Files Section */}
      <Grid>
        <Grid.Col span={mode === "create" ? 12 : 6}>
          <Paper p="md" withBorder>
            <Title order={6} mb="md" c="blue">
              {getTranslation("TITLE_FILES") || "Files"}
            </Title>
            {mode === "view" && canShowOtherFiles ? (
              <Stack gap="md">
                <ViewModeFileList
                  files={form.values.existingFiles}
                  onFileRemove={(fileId) => {
                    const updatedFiles = form.values.existingFiles.filter(
                      (f) => f.id !== fileId,
                    );
                    const updatedKeepIds = form.values.fileIdsToKeep.filter(
                      (id) => id !== fileId,
                    );
                    form.setFieldValue("existingFiles", updatedFiles);
                    form.setFieldValue("fileIdsToKeep", updatedKeepIds);
                  }}
                  label={
                    getTranslation("LABEL_EXISTING_FILES") || "Existing Files"
                  }
                  description={
                    getTranslation("LABEL_DOWNLOAD_OR_REMOVE_FILES") ||
                    "Download or remove existing files"
                  }
                  disabled={!canModify || !canModifyOtherFiles}
                />
                <FileUploadList
                  files={form.values.attachedFiles}
                  onChange={(files) =>
                    form.setFieldValue("attachedFiles", files)
                  }
                  label={
                    getTranslation("LABEL_UPLOAD_NEW_FILES") ||
                    "Upload New Files"
                  }
                  description={
                    getTranslation("LABEL_ADD_NEW_FILES_TO_OBJECT") ||
                    "Add new files to this object"
                  }
                  disabled={!canModify || !canModifyOtherFiles}
                />
              </Stack>
            ) : (
              <FileUploadList
                files={form.values.attachedFiles}
                onChange={(files) => form.setFieldValue("attachedFiles", files)}
                label={
                  form.values.attachedFiles.length > 0
                    ? getTranslation("LABEL_ADDED_FILES") || "Added Files"
                    : getTranslation("LABEL_ADD_FILES") || "Add Files"
                }
                description={
                  getTranslation("LABEL_UPLOAD_MULTIPLE_FILES_WITH_HEADERS") ||
                  "You can upload multiple files with file headers"
                }
                disabled={!canModify || !canModifyOtherFiles}
              />
            )}
          </Paper>
        </Grid.Col>

        {mode === "view" && canShowQualityControlFiles && (
          <Grid.Col span={6}>
            <Paper p="md" withBorder>
              <Title order={6} mb="md" c="blue">
                {getTranslation("TITLE_COATING_RESEARCH_REPORT") ||
                  "Coating Research Report"}
              </Title>
              <Stack gap="md">
                <ViewModeFileList
                  files={form.values.existingQcFiles}
                  onFileRemove={(fileId) => {
                    const updatedFiles = form.values.existingQcFiles.filter(
                      (f) => f.id !== fileId,
                    );
                    const updatedKeepIds = form.values.qcFileIdsToKeep.filter(
                      (id) => id !== fileId,
                    );
                    form.setFieldValue("existingQcFiles", updatedFiles);
                    form.setFieldValue("qcFileIdsToKeep", updatedKeepIds);
                  }}
                  label={
                    getTranslation("LABEL_EXISTING_COATING_RESEARCH_REPORTS") ||
                    "Existing Coating Research Reports"
                  }
                  description={
                    getTranslation("LABEL_DOWNLOAD_OR_REMOVE_REPORTS") ||
                    "Download or remove existing reports"
                  }
                  disabled={!canModify || !canModifyQualityControlFiles}
                />
                <FileUploadList
                  files={form.values.coatingResearchReport}
                  onChange={(files) =>
                    form.setFieldValue("coatingResearchReport", files)
                  }
                  label={
                    getTranslation("LABEL_UPLOAD_NEW_REPORTS") ||
                    "Upload New Reports"
                  }
                  description={
                    getTranslation("LABEL_ADD_NEW_COATING_REPORTS") ||
                    "Add new coating research reports"
                  }
                  disabled={!canModify || !canModifyQualityControlFiles}
                />
                {!canModifyQualityControlFiles && (
                  <Text size="sm" c="grey">
                    {getTranslation("LABEL_NO_PERMISSION_COATING_REPORTS") ||
                      "You do not have permission to modify coating research reports."}
                  </Text>
                )}
              </Stack>
            </Paper>
          </Grid.Col>
        )}
      </Grid>

      {/* Modification History and QC Coordinates Section */}
      {mode === "view" && (
        <Grid>
          {!hasStaraObjectFields && (
          <Grid.Col span={6}>
            <Paper p="md" withBorder>
              <Title order={6} mb="md" c="blue">
                {getTranslation("TITLE_MODIFICATION_HISTORY") ||
                  "Modification History"}
              </Title>
              <Grid>
                <Grid.Col span={12}>
                  <TextInput
                    label={getTranslation("LABEL_MODIFIED_BY") || "Modified By"}
                    value={form.values.modifier || ""}
                    disabled
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <TextInput
                    label={
                      getTranslation("LABEL_MODIFIED_DATE") || "Modified Date"
                    }
                    value={
                      form.values.modified
                        ? new Date(form.values.modified).toLocaleString()
                        : ""
                    }
                    disabled
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>
          )}
          <Grid.Col span={hasStaraObjectFields ? 12 : 6}>
            <Paper p="md" withBorder>
              <Title order={6} mb="md" c="blue">
                {getTranslation("TITLE_DRILL_BITS_COORDINATES") ||
                  "Coordinate of Drill bits (ETRS-GK25)"}
              </Title>
              <Grid>
                <Grid.Col span={12}>
                  <NumberInput
                    label={
                      getTranslation("LABEL_X_COORDINATE") || "X Coordinate"
                    }
                    placeholder={
                      getTranslation("PLACEHOLDER_ENTER_X_COORDINATE") ||
                      "Enter X coordinate"
                    }
                    // description="Quality control X coordinate"
                    value={form.values.drillLocation?.xcoord ?? ""}
                    onChange={(value) => {
                      const numValue = value === "" ? null : Number(value);
                      form.setFieldValue("drillLocation", {
                        xcoord: numValue,
                        ycoord: form.values.drillLocation?.ycoord ?? null,
                      });
                    }}
                    decimalScale={2}
                    step={0.01}
                    disabled={!canModify}
                  />
                </Grid.Col>
                <Grid.Col span={12}>
                  <NumberInput
                    label={
                      getTranslation("LABEL_Y_COORDINATE") || "Y Coordinate"
                    }
                    placeholder={
                      getTranslation("PLACEHOLDER_ENTER_Y_COORDINATE") ||
                      "Enter Y coordinate"
                    }
                    // description="Quality control Y coordinate"
                    value={form.values.drillLocation?.ycoord ?? ""}
                    onChange={(value) => {
                      const numValue = value === "" ? null : Number(value);
                      form.setFieldValue("drillLocation", {
                        xcoord: form.values.drillLocation?.xcoord ?? null,
                        ycoord: numValue,
                      });
                    }}
                    decimalScale={2}
                    step={0.01}
                    disabled={!canModify}
                  />
                </Grid.Col>
              </Grid>
            </Paper>
          </Grid.Col>
        </Grid>
      )}

      {/* User Rights Section - Commented out as it is currently not in use. Can be added back at a later date. */}
      {/* <Paper p="md" withBorder>
        <Title order={6} mb="md" c="blue">
          {getTranslation("TITLE_USAGE_RIGHTS") || "Usage Rights"}
        </Title>
        <Paper p="sm" withBorder>
          <Stack gap="xs">
            <Radio
              label={
                getTranslation("LABEL_GENERAL_USAGE_RIGHTS") ||
                "General Usage Rights"
              }
              value="general"
              checked={form.values.userRightsType === "general"}
              onChange={() => form.setFieldValue("userRightsType", "general")}
              disabled={!canModify}
            />
            <Radio
              label={
                getTranslation("LABEL_SITESPECIFIC_USAGE_RIGHTS") ||
                "Site-Specific Usage Rights"
              }
              value="site-specific"
              checked={form.values.userRightsType === "site-specific"}
              onChange={() =>
                form.setFieldValue("userRightsType", "site-specific")
              }
              disabled={!canModify}
            />
          </Stack>
        </Paper>

        {form.values.userRightsType === "site-specific" && (
          <Paper mt="md" p="md" withBorder>
            <Text size="sm" fw={500} mb="xs">
              {getTranslation("LABEL_ROLE_PERMISSIONS") || "Role Permissions"}
            </Text>
            {isLoadingRoles ? (
              <Text size="sm" c="dimmed">
                {getTranslation("LABEL_LOADING_ROLES") || "Loading roles..."}
              </Text>
            ) : availableRoles.length === 0 ? (
              <Text size="sm" c="dimmed">
                {getTranslation("LABEL_NO_ROLES_AVAILABLE") ||
                  "No roles available for this address system"}
              </Text>
            ) : (
              <Table
                striped
                highlightOnHover
                withRowBorders={false}
                styles={{
                  thead: {
                    borderBottom: "2px solid var(--mantine-color-gray-3)",
                  },
                }}
              >
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>
                      {getTranslation("LABEL_USER_GROUP") || "User Group"}
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Group justify="center">
                        <Tooltip
                          label={
                            getTranslation("LABEL_NO_ACCESS") || "No Access"
                          }
                        >
                          <IconLock size={20} />
                        </Tooltip>
                      </Group>
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Group justify="center">
                        <Tooltip
                          label={
                            getTranslation("LABEL_READ_ONLY") || "Read Only"
                          }
                        >
                          <IconLockOpen size={20} />
                        </Tooltip>
                      </Group>
                    </Table.Th>
                    <Table.Th style={{ textAlign: "center" }}>
                      <Group justify="center">
                        <Tooltip
                          label={
                            getTranslation("LABEL_READWRITE") || "Read/Write"
                          }
                        >
                          <IconKey size={20} />
                        </Tooltip>
                      </Group>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...form.values.rolePermissions]
                    .sort((a, b) => a.roleId - b.roleId)
                    .map((permission) => {
                      const index = form.values.rolePermissions.findIndex(
                        (p) => p.roleId === permission.roleId,
                      );
                      return (
                        <Table.Tr key={permission.roleId}>
                          <Table.Td>{permission.roleName}</Table.Td>
                          <Table.Td>
                            <Group justify="center">
                              <Radio
                                checked={permission.accessLevel === 0}
                                onChange={() => {
                                  const updated = [
                                    ...form.values.rolePermissions,
                                  ];
                                  updated[index].accessLevel = 0;
                                  form.setFieldValue(
                                    "rolePermissions",
                                    updated,
                                  );
                                }}
                                disabled={!canModify}
                              />
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Group justify="center">
                              <Radio
                                checked={permission.accessLevel === 1}
                                onChange={() => {
                                  const updated = [
                                    ...form.values.rolePermissions,
                                  ];
                                  updated[index].accessLevel = 1;
                                  form.setFieldValue(
                                    "rolePermissions",
                                    updated,
                                  );
                                }}
                                disabled={!canModify}
                              />
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Group justify="center">
                              <Radio
                                checked={permission.accessLevel === 2}
                                onChange={() => {
                                  const updated = [
                                    ...form.values.rolePermissions,
                                  ];
                                  updated[index].accessLevel = 2;
                                  form.setFieldValue(
                                    "rolePermissions",
                                    updated,
                                  );
                                }}
                                disabled={!canModify}
                              />
                            </Group>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                </Table.Tbody>
              </Table>
            )}
          </Paper>
        )}
      </Paper> */}

      {/* Copy to New Destination Section */}
      {mode === "view" && (
        <Paper p="md" withBorder>
          <Title order={6} mb="md" c="blue">
            {getTranslation("TITLE_COPY_TO_NEW_DESTINATION") ||
              "Copy to New Destination"}
          </Title>
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <Select
                  label={
                    getTranslation("LABEL_TARGET_CONTRACT") || "Target Contract"
                  }
                  placeholder={
                    isLoadingJobs
                      ? getTranslation("LABEL_LOADING_CONTRACTS") ||
                        "Loading contracts..."
                      : jobOptions.length === 0
                        ? getTranslation("LABEL_NO_CONTRACTS_AVAILABLE") ||
                          "No contracts available"
                        : getTranslation(
                            "PLACEHOLDER_SELECT_TARGET_CONTRACT",
                          ) || "Select target contract"
                  }
                  data={jobOptions}
                  disabled={
                    !canModify ||
                    isLoadingJobs ||
                    jobOptions.length === 0 ||
                    isCopying
                  }
                  required
                  clearable
                  {...getSelectProps("targetContract")}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <TextInput
                  label={
                    getTranslation("LABEL_THE_NAME_OF_THE_NEW_OBJECT") ||
                    "The Name of the new Object"
                  }
                  placeholder={
                    getTranslation("PLACEHOLDER_ENTER_NEW_OBJECT_NAME") ||
                    "Enter new object name"
                  }
                  required
                  disabled={!canModify || isCopying}
                  {...form.getInputProps("newObjectName")}
                />
              </Grid.Col>
            </Grid>
            <Group justify="flex-end">
              <Button
                leftSection={<IconCopy size={16} />}
                onClick={handleCopyToContract}
                disabled={
                  !canModify ||
                  !form.values.targetContract ||
                  !form.values.newObjectName ||
                  isCopying
                }
                loading={isCopying}
              >
                {getTranslation("LABEL_COPY_TO_CONTRACT") || "Copy to Contract"}
              </Button>
            </Group>
          </Stack>
        </Paper>
      )}

      {/* <Alert
                    icon={<IconInfoCircle size={16} />}
                    title={getTranslation("TITLE_NOTE") || "Note"}
                    color="blue"
                    variant="light"
                >
                    {mode === 'create' 
                        ? 'This form allows you to create a new action.'
                        : 'This form allows you to edit object properties.'
                    }
                </Alert> */}

      {mode === "create" ? (
        <Group grow mt="md" style={stickyButtons ? {
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'var(--mantine-color-body)',
          paddingTop: 8,
          paddingBottom: 8,
          borderTop: '1px solid var(--mantine-color-gray-3)',
          zIndex: 10,
        } : undefined}>
          <Button
            onClick={() => handleSubmit(form.values)}
            loading={isUpdating}
            disabled={isUpdating}
          >
            {getTranslation("LABEL_CREATE_ACTION") || "Create Action"}
          </Button>
          <Button variant="light" onClick={handleClose}>
            {getTranslation("LABEL_CLEAR") || "Clear"}
          </Button>
        </Group>
      ) : (
        <Group justify="space-between">
          <Group>
            <Button
              variant="outline"
              color="blue"
              leftSection={<IconPrinter size={16} />}
              onClick={printPlannedWorkSteps}
            >
              Create Object Report
            </Button>
          </Group>
          <Group>
            <Button
              onClick={handleSaveAndRefresh}
              loading={isUpdating}
              disabled={!canModify || isUpdating}
              style={{ width: "150px" }}
            >
              {getTranslation("LABEL_UPDATE_OBJECT") || "Update Object"}
            </Button>
            <Button
              variant="light"
              onClick={handleClose}
              style={{ width: "150px" }}
            >
              {getTranslation("LABEL_CLEAR") || "Clear"}
            </Button>
          </Group>
        </Group>
      )}
    </Stack>
  );
}
